import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Fade,
  Grid,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { solicitarValidacaoEmail } from '../../services/api';
import toast from 'react-hot-toast';

const ValidationPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [token, setToken] = useState('');
  
  // Pegar dados do state (vindo da SearchPage)
  const servidorData = location.state?.servidorData;

  useEffect(() => {
    if (!servidorData) {
      navigate('/consulta');
    }
  }, [servidorData, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      toast.loading('Enviando código de validação...', { id: 'validation' });
      const response = await solicitarValidacaoEmail(servidorData.servidor.cpf, email);
      
      setToken(response.token);
      setSuccess(true);
      toast.success('Código enviado para seu email!', { id: 'validation' });
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || 'Erro ao enviar código de validação';
      setError(errorMsg);
      toast.error(errorMsg, { id: 'validation' });
    } finally {
      setLoading(false);
    }
  };

  const handleProceedToValidation = () => {
    // Redirecionar para página de inserção do código
    navigate(`/auth/${token}`);
  };

  if (!servidorData) {
    return null;
  }

  return (
    <Fade in={true} timeout={500}>
      <Box sx={{ maxWidth: 600, mx: 'auto' }}>
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            borderRadius: 3,
            textAlign: 'center',
          }}
        >
          <EmailIcon 
            sx={{ 
              fontSize: 60, 
              color: 'primary.main',
              mb: 2 
            }} 
          />
          
          {!success ? (
            <>
              <Typography variant="h5" gutterBottom color="primary.main">
                Confirmação de Email
              </Typography>
              
              <Typography variant="body1" paragraph color="text.secondary">
                Para prosseguir com a assinatura dos documentos, precisamos confirmar seu email.
                Enviaremos um código de 6 dígitos para o email informado.
              </Typography>
              
              <Box sx={{ my: 3, p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {servidorData.servidor.nome_completo}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  CPF: {servidorData.servidor.cpf}
                </Typography>
              </Box>

              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Digite seu e-mail"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  variant="outlined"
                  margin="normal"
                  required
                  disabled={loading}
                  InputProps={{
                    sx: { borderRadius: 2 }
                  }}
                />

                {error && (
                  <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                    {error}
                  </Alert>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading || !email}
                  sx={{ 
                    mt: 3, 
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 'bold'
                  }}
                >
                  {loading ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Enviando...
                    </>
                  ) : (
                    'Enviar Código de Validação'
                  )}
                </Button>
              </form>
            </>
          ) : (
            <>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: 'success.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 50, color: 'success.main' }} />
              </Box>
              
              <Typography variant="h4" gutterBottom color="success.main">
                Código Enviado!
              </Typography>
              
              <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                Um código de 6 dígitos foi enviado para: <strong>{email}</strong>
              </Alert>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Box sx={{ p: 2, border: '1px dashed', borderColor: 'primary.main', borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Vencimento:
                    </Typography>
                    <Typography variant="body2">
                      15 minutos
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ p: 2, border: '1px dashed', borderColor: 'primary.main', borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Verifique:
                    </Typography>
                    <Typography variant="body2">
                      Caixa de spam
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Typography variant="body1" paragraph>
                Verifique sua caixa de entrada e spam. O código tem validade de 15 minutos.
                Após receber o código, clique no botão abaixo para inseri-lo.
              </Typography>

              <Button
                variant="contained"
                size="large"
                onClick={handleProceedToValidation}
                sx={{
                  px: 6,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  borderRadius: 2,
                  background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                }}
              >
                Inserir Código Recebido
              </Button>
            </>
          )}
        </Paper>
      </Box>
    </Fade>
  );
};

export default ValidationPage;